import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { getAdminBanners, createBanner, updateBanner, deleteBanner, uploadFile } from '../../services/api';
import { toast } from 'sonner';

const BannersAdmin = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', subtitle: '', image: '', link: '', button_text: '', order: 0, is_active: true });
  const [imageFile, setImageFile] = useState(null);

  const fetchData = async () => {
    try {
      const res = await getAdminBanners();
      setBanners(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', subtitle: '', image: '', link: '', button_text: '', order: 0, is_active: true });
    setImageFile(null);
    setDialogOpen(true);
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setForm({ title: banner.title, subtitle: banner.subtitle || '', image: banner.image, link: banner.link || '', button_text: banner.button_text || '', order: banner.order || 0, is_active: banner.is_active !== false });
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = form.image;
      if (imageFile) {
        const uploadRes = await uploadFile(imageFile, 'banner');
        imageUrl = uploadRes.data.url;
      }
      const payload = { ...form, image: imageUrl };
      if (editing) {
        await updateBanner(editing.id, payload);
        toast.success('Banner updated');
      } else {
        await createBanner(payload);
        toast.success('Banner created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await deleteBanner(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="banners-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-sm text-gray-500">{banners.length} banners</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-banner-btn">
          <Plus size={16} className="mr-2" /> Add Banner
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-20" /></Card>)}</div>
      ) : banners.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No banners yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {banners.map((banner) => (
            <Card key={banner.id} className="hover:shadow-sm transition-shadow" data-testid={`banner-${banner.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                {banner.image && (
                  <img src={banner.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${banner.image}` : banner.image} alt={banner.title} className="w-24 h-14 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{banner.title}</h3>
                    {!banner.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                  </div>
                  {banner.subtitle && <p className="text-xs text-gray-500 mt-0.5">{banner.subtitle}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(banner)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(banner.id)} className="text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Banner</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="banner-title-input" /></div>
            <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></div>
            <div>
              <Label>Image</Label>
              <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className="text-sm" />
              {!imageFile && form.image && <p className="text-[10px] text-gray-400 mt-1">Current: {form.image}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Link URL</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/products" /></div>
              <div><Label>Button Text</Label><Input value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="Shop Now" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="banner-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannersAdmin;
